/*
 * Copyright (c) 2025 curryfirm.com
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import {
    fetchDumpstersThunk,
    selectDumpstersAreLoading,
    selectDumpsterError,
    selectAllDumpsters,
    selectDumpstersNotAtLot,
} from '../../../features/dumpsters/dumpstersSlice.js';
import {
    selectChosenDumpsterIds,
    setChosenDumpsterIds,
    selectReturnToSummaryAvailable,
    setReturnToSummaryAvailable, setStep, WIZARD_STEPS, selectStoredRouteType,
} from '../../../features/workflow/workflowSlice.js';
import * as constants from '../../../constants.js';
import {ROUTE_TYPES} from "../../../constants.js";
import {selectEmptyAvailableDumpsterDetails} from "../../../features/workflow/workflowSelectors.js";
import {getSwapType} from "../../../features/dumpsters/utils.js";

const ChooseDumpsters = ({ onBack, onContinue }) => {
    const dispatch = useDispatch();

    const storedIds = useSelector(selectChosenDumpsterIds);
    const choicesChanged = (chosenIds) => {
        return JSON.stringify(storedIds) !== JSON.stringify(chosenIds);
    }

    // Selectors for global data and inventory
    const dumpsters = useSelector(selectAllDumpsters);
    const dumpstersNotAtLot = useSelector(selectDumpstersNotAtLot);
    const loading = useSelector(selectDumpstersAreLoading);
    const error = useSelector(selectDumpsterError);

    // Selectors for disabling incompatible dumpsters if swap and drop.
    const isSwapAndDrop = (useSelector(selectStoredRouteType) === ROUTE_TYPES.SWAP_AND_DROP);
    const emptyCanArray = useSelector(selectEmptyAvailableDumpsterDetails);
    const emptyCanType = emptyCanArray?.[0] ? getSwapType(emptyCanArray[0]) : undefined;

    // Selector for workflow/session choice state
    const returnToSummaryAvailable = useSelector(selectReturnToSummaryAvailable);

    const [chosenIds, setChosenIds] = useState(
        storedIds.length > 0 ? storedIds : []
    );

    // Insure inventory is loaded.
    useEffect(() => {
        // Load dumpsters if not present
        if (dumpsters.length === 0 && !loading) {
            dispatch(fetchDumpstersThunk());
        }
    }, [dispatch, dumpsters.length, loading]);
    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (loading) {
        return <Typography variant="body1">Loading dumpsters...</Typography>;
    }

    const toggleSelection = (id) => {
        setChosenIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    // Validation: enforce choosing at least one, up to MAX
    const canContinue = chosenIds.length > 0 && chosenIds.length <= constants.MAX_DUMPSTERS_TO_ROUTE;

    const canIsIncompatibleWithEmpty = dumpster => {
        const thisCanType = getSwapType(dumpster);
        const result = isSwapAndDrop && (thisCanType !== emptyCanType);
        return result;
    }

    const handleLeaveStep = (direction) => {
        if (choicesChanged(chosenIds)) dispatch(setChosenDumpsterIds(chosenIds));
        direction === constants.FORWARD ? onContinue() : onBack();
    };

    const handleReturnToSummary = () => {
        if (choicesChanged(chosenIds)) dispatch(setChosenDumpsterIds(chosenIds));
        dispatch(setStep(WIZARD_STEPS.SUMMARY));
        dispatch(setReturnToSummaryAvailable(false)); // Reset flag on return
    }

    return (
        <Box>
            <Typography variant="h4">Select Cans to Haul</Typography>
            <List>
                {dumpstersNotAtLot.map(dumpster => (
                    <ListItem
                        key={dumpster.dumpsterId}
                        secondaryAction={
                            <Checkbox
                                edge="end"
                                checked={chosenIds.includes(dumpster.dumpsterId)}
                                onChange={() => toggleSelection(dumpster.dumpsterId)}
                                disabled={
                                    (!chosenIds.includes(dumpster.dumpsterId) &&
                                        chosenIds.length >= constants.MAX_DUMPSTERS_TO_ROUTE)
                                    || canIsIncompatibleWithEmpty(dumpster)
                                }
                            />
                        }
                    >
                        <Typography variant="body1">
                            {dumpster.dumpsterId}: {dumpster.customer}, {dumpster.street}, {dumpster.city}
                        </Typography>
                    </ListItem>
                ))}
            </List>
            <Box sx={{ mt: 3, display: "flex", }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleLeaveStep(constants.BACK)}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleLeaveStep(constants.FORWARD)}
                    disabled={!canContinue}
                    sx={{ ml: 2 }}
                >
                    Next
                </Button>

                {/* Render a "Return to Summary" button conditionally, basd on store */}
                { returnToSummaryAvailable && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleReturnToSummary}
                        sx={{ ml: 2 }}
                    >
                        Return to Summary
                    </Button>
                )}
            </Box>
            <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                Select up to {constants.MAX_DUMPSTERS_TO_ROUTE} dumpsters to route.
            </Typography>
        </Box>
    );
};

export default ChooseDumpsters;
